<template>
  <div id="userEdit">
    <div class="container">

      <PageTitle :title="user.firstName || user.lastName ? fullName : $t('loyalty_accounts.user')">
        <button class="save btn btn-primary" @click="save" v-if="$store.getters.loggedUserHasModifyPermission">
          {{ $t("save") }}
        </button>
      </PageTitle>

      <Tabs>
        <TabSection :title="$t('user.account_information')">
          <!-- Required base fields -->
          <div class="columns">
            <div class="column">
              <div class="group">
                <label>{{ $t('user.username') }}</label>
                <input type="text" v-model="user.username">
              </div>
              <div class="group">
                <label>{{ $t('user.email') }}</label>
                <input type="email" v-model="user.email" :class="{ invalid: $validate.error('user.email') }">
              </div>
              <div class="group">
                <label>{{ $t('user.first_name') }}</label>
                <input type="text" v-model="user.firstName" :class="{ invalid: $validate.error('user.firstName') }">
              </div>
              <div class="group">
                <label>{{ $t('user.last_name') }}</label>
                <input type="text" v-model="user.lastName" :class="{ invalid: $validate.error('user.lastName') }">
              </div>
            </div>
            <div class="column">
              <div class="group">
                <label>{{ $t('user.role') }}</label>
                <select v-model="user.role">
                  <option v-for="role of availableRoles" :value="role.id">{{ role.name }}</option>
                </select>
              </div>
              <div class="group">
                <label>{{ $t('user.password') }}</label>
                <input type="password" v-model="user.password">
              </div>
            </div>
          </div>

          <Panel :title="$t('settings.permissions')" v-if="isDistributor">
            <div class="columns">
              <div class="column">
                <div class="group">
                  <input id="chkCanModify" type="checkbox" v-model="user.info.canModify">
                  <label for="chkCanModify">{{ $t("user.can_modify") }}</label>
                </div>
                <div class="group">
                  <input id="chkCanViewPaymentSettings" type="checkbox" v-model="user.info.canViewPaymentSettings">
                  <label for="chkCanViewPaymentSettings">{{ $t("user.can_view_payment_settings") }}</label>
                </div>
                <div class="group" :class="{ locked: !user.info.canViewPaymentSettings }">
                  <input id="chkCanEditPaymentSettings" type="checkbox" v-model="user.info.canEditPaymentSettings">
                  <label for="chkCanEditPaymentSettings">{{ $t("user.can_edit_payment_settings") }}</label>
                </div>
              </div>
            </div>
          </Panel>

          <Panel :title="$t('user.access_configuration')" v-if="hasMultiBranchesField">
            <div class="columns">
              <div class="column">
                <div class="group">
                  <label>{{ $t('user.serverBranches') }}</label>
                  <AjaxSelectMulti v-model="user.serverBranches" api="/api/server_branches"
                    :mapFn="c => `${c.server.name} - ${c.name}`" :allowEmptySearch="true" />
                </div>
              </div>
              <div class="column">
                <div class="group">
                  <label>{{ $t('user.serverAccess') }}</label>
                  <AjaxSelectMulti v-model="user.serverAccess" api="/api/server_accesses" :mapFn="a => a.text"
                    :allowEmptySearch="true" />
                </div>
              </div>
            </div>
          </Panel>

          <Panel :title="$t('user.additional_configuration')" v-if="isDistributor">
            <div class="columns">
              <div class="column">
                <div class="group">
                  <label>{{ $t('user.info.company') }}</label>
                  <input type="text" v-model="user.info.company">
                </div>
                <div class="group">
                  <label>{{ $t('user.info.POS') }}</label>
                  <input type="text" v-model="user.info.POS">
                </div>
                <div class="group">
                  <label>{{ $t('user.info.phone') }}</label>
                  <input type="text" v-model="user.info.phone">
                </div>
              </div>
              <div class="column">
                <div class="group">
                  <label>{{ $t('user.info.address') }}</label>
                  <input type="text" v-model="user.info.address">
                </div>
                <div class="group">
                  <label>{{ $t('user.info.region') }}</label>
                  <input type="text" v-model="user.info.region">
                </div>
                <div class="group">
                  <label>{{ $t('user.info.country') }}</label>
                  <input type="text" v-model="user.info.country">
                </div>
              </div>
            </div>
          </Panel>

        </TabSection>
      </Tabs>

      <div class="footer-actions" v-if="$store.getters.loggedUserHasModifyPermission">
        <button class="btn btn-primary" @click="save">{{ $t("save") }}</button>
      </div>

    </div>

    <Modal name="save-error" :title="$t('error.title')" :actions="[{ name: $t('back'), action: 'close' }]">
      {{ $t('branch.error.save') }}
    </Modal>

    <Modal name="save-required" :title="$t('error.title')" :actions="[{ name: $t('back'), action: 'close' }]">
      {{ $t('branch.error.required') }}
    </Modal>

    <Modal name="save-success" :title="$t('success')" :actions="[{ name: $t('continue'), action: 'close' }]">
      {{ $t('branch.success') }}
    </Modal>

  </div>
</template>

<script>
  import AjaxSelectMulti from "@/components/tools/AjaxSelectMulti";
  import { mapActions } from 'vuex';
  import PageTitle from "@/components/PageTitle";

  export default {
    name: 'userEdit',

    components: { AjaxSelectMulti, PageTitle },

    data() {
      return {
        user: {
          id: null,
          role: "",
          firstName: "",
          lastName: "",
          username: "",
          email: "",
          info: {
            canModify: false,
            canViewPaymentSettings: false,
            canEditPaymentSettings: false
          },
          password: null,
          company: this.$route.query.company,
          branch: this.$route.query.branch,
          serverBranches: [],
          serverAccess: [],
        },
        isDispatchActivated: false,
        isPosActivated: false,
      }
    },

    computed: {
      fullName() {
        return `${this.user.firstName} ${this.user.lastName}`;
      },
      hasMultiBranchesField() {
        return this.isDistributor
      },
      isDistributor() {
        return this.user.role === 'ROLE_DISTRIBUTOR'
      },
      atLeastSuperAdmin() {
        return this.$store.getters.loggedUserHasSuperAdmin;
      },
      availableRoles() {
        let branchId = this.$route.query.branch;
        let companyId = this.$route.query.company;

        let roles = [];
        if (!branchId && !companyId) {
          roles.push({ id: 'ROLE_SUPER_ADMIN', name: this.$t('roles.ROLE_SUPER_ADMIN') });
          roles.push({ id: 'ROLE_DISTRIBUTOR', name: this.$t('roles.ROLE_DISTRIBUTOR') });
        } else if (branchId && !companyId) {
          if (this.atLeastSuperAdmin) {
            roles.push({ id: 'ROLE_ADMIN', name: this.$t('roles.ROLE_BRANCH_ADMIN') });
          }
          roles.push({ id: 'ROLE_RECEPTION', name: this.$t('roles.ROLE_RECEPTION') });
          if (this.isDispatchActivated && this.atLeastSuperAdmin) {
            roles.push({ id: 'ROLE_DISPATCH', name: this.$t('roles.ROLE_DISPATCH') });
          }
        } else if (branchId && companyId) {
          roles.push({ id: 'ROLE_CLERK', name: this.$t('roles.ROLE_CLERK') });
          roles.push({ id: 'ROLE_ADMIN', name: this.$t('roles.ROLE_COMPANY_ADMIN') });
          roles.push({ id: 'ROLE_RECEPTION', name: this.$t('roles.ROLE_RECEPTION') });
          if (this.isPosActivated) {
            roles.push({ id: 'ROLE_POS', name: this.$t('roles.ROLE_POS') })
          }
        }
        return roles;
      }
    },

    watch: {
      "user.info.canViewPaymentSettings"() {
        if (!this.user.info.canViewPaymentSettings) {
          this.user.info.canEditPaymentSettings = false;
        }
      }
    },

    validate: {
      "user.email": {
        required: true,
        email: true
      },
      "user.firstName": {
        required: true
      },
      "user.lastName": {
        required: true
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "users.title";
      this.$store.state.pageIcon = "";
      if (this.$route.query.user) {
        let user = await this.getUser({ id: this.$route.query.user });
        for (let key of Object.keys(this.user)) {
          this.user[key] = user[key];
        }
        if (!this.user.info) {
          this.user.info = {
            canModify: false,
            canEditPaymentSettings: false,
            canViewPaymentSettings: false
          }
        }
      } else {
        this.user.role = this.availableRoles[0].id
      }
      if (this.$route.query.branch) {
        let branchModule = await this.$store.getters.getContextBranchActivatedModule;
        this.isDispatchActivated = branchModule ? branchModule.dispatch : false;
      }
      if (this.$route.query.branch && this.$route.query.company) {
        let branchModule = await this.$store.getters.getContextBranchActivatedModule;
        this.isPosActivated = branchModule ? branchModule.POS : false;
      }
      if (!this.$store.getters.loggedUserHasModifyPermission) {
        window.lockFields();
      }
    },

    methods: {

      ...mapActions(["getUser", "saveUser", "createUser"]),

      async save() {
        if (!this.$validate.valid()) {
          this.$modal.show("save-required");
          this.$validate.validateAllOnce();
          return;
        }
        if (!this.user.password || !this.user.password.trim()) {
          this.user.password = undefined
        }
        if (!this.user.username) {
          this.user.username = this.user.email
        }
        let clone = JSON.parse(JSON.stringify(this.user));
        if (!this.isDistributor) {
          delete clone.info.canModify;
          delete clone.info.canEditPaymentSettings;
          delete clone.info.canViewPaymentSettings;
        }
        clone.serverBranches = this.hasMultiBranchesField
          ? clone.serverBranches.map(sb => sb.id)
          : [];

        this.$store.commit("showSpinner", true);
        let response = this.user.id
          ? await this.saveUser({ id: this.user.id, data: clone })
          : await this.createUser({ data: this.user });

        this.$store.commit("showSpinner", false);

        if (response) {
          this.user = response;
          toast(this.$t("users.save_success"), "success");
          await this.$router.push(this.$router.buildContext(`/users/${response.role}`));
        } else {
          toast(this.$t("error.no_server"), "error");
        }
      }
    },
  };
</script>

<style lang="scss" scoped>
  #userEdit {
    display: flex;
    flex-direction: column;
  }
</style>